import {useChat} from '@ai-sdk/react';
import {DefaultChatTransport} from 'ai';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {reconcileCampaignTranscript} from '../domain/reconcileCampaignTranscript';
import {needsServerMessageHydration} from '../domain/campaignMessageHydration';
import {messagesFingerprint} from '../domain/toolPartSelectors';
import {useTranslation} from 'react-i18next';
import useAgentSessionStore from '../store/agentSessionStore';
import {getAuthToken} from '../services/authToken';

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

export default function useAgentChat(sessionLoader) {
  const {t} = useTranslation();
  const {activeSessionId, addSession, setActiveSession} =
    useAgentSessionStore();
  const sessionIdRef = useRef(activeSessionId);
  const previousActiveSessionIdRef = useRef(activeSessionId);
  const hydratedSessionIdRef = useRef(null);
  const creatingSessionFromSendRef = useRef(false);
  const onCampaignLimitReachedRef = useRef(null);
  sessionIdRef.current = activeSessionId;

  const {sessionDetail, refetch} = sessionLoader;
  const previousChatStatusRef = useRef('ready');

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${BASE_URL}agent-sessions/chat`,
        credentials: 'include',
        headers: () => {
          const token = getAuthToken();
          if (!token) {
            throw new Error('Authentication token is missing.');
          }
          return {Authorization: 'Bearer ' + token};
        },
        body: () => ({
          sessionId: sessionIdRef.current || undefined,
        }),
        fetch: async (url, options) => {
          const response = await fetch(url, options);

          // Intercept 403 BEFORE AI SDK processes it
          if (!response.ok && response.status === 403) {
            const body = await response.text();
            const err = new Error(body);
            err.status = 403;
            throw err;
          }

          if (!response.ok && response.status === 409) {
            const body = await response.text();
            const err = new Error(body);
            err.status = 409;
            throw err;
          }

          const newSessionId = response.headers.get('X-Session-Id');
          if (newSessionId) {
            addSession({
              uuid: newSessionId,
              title: t('campaignChat.newConversation'),
              status: 'active',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
            setActiveSession(newSessionId);
            sessionIdRef.current = newSessionId;
          }
          return response;
        },
      }),
    [addSession, setActiveSession, t],
  );

  const {
    messages,
    setMessages,
    sendMessage: chatSendMessage,
    status,
    error,
    regenerate,
    clearError,
  } = useChat({
    transport,
    onError: (err) => {
      if (err.status === 409) {
        void refetch();
        return;
      }
      if (err.status === 403) {
        try {
          const body = JSON.parse(err.message);
          if (body?.error?.details?.code === 'campaign_art_limit_reached') {
            onCampaignLimitReachedRef.current?.({
              available: body.error.details.available,
              required: body.error.details.required,
            });
            return;
          }
        } catch {
          // parse fail, treat as generic error
        }
      }
    },
  });

  useEffect(() => {
    const previousSessionId = previousActiveSessionIdRef.current;
    const hasSessionChanged = previousSessionId !== activeSessionId;

    if (!hasSessionChanged) {
      return;
    }

    const isCreatingSessionFromFirstMessage =
      creatingSessionFromSendRef.current &&
      !previousSessionId &&
      !!activeSessionId;

    if (!isCreatingSessionFromFirstMessage) {
      setMessages([]);
    }

    if (!activeSessionId) {
      creatingSessionFromSendRef.current = false;
    }

    hydratedSessionIdRef.current = null;
    previousActiveSessionIdRef.current = activeSessionId;
  }, [activeSessionId, setMessages]);

  useEffect(() => {
    if (!activeSessionId) {
      return;
    }

    if (!sessionDetail || sessionDetail.uuid !== activeSessionId) {
      return;
    }

    if (status === 'submitted' || status === 'streaming' || status === 'error') {
      return;
    }

    const storedMessages = Array.isArray(sessionDetail.messages)
      ? sessionDetail.messages
      : [];

    if (creatingSessionFromSendRef.current && storedMessages.length === 0) {
      hydratedSessionIdRef.current = activeSessionId;
      return;
    }

    const isSessionSwitch = hydratedSessionIdRef.current !== activeSessionId;

    const shouldReconcile =
      isSessionSwitch ||
      messages.length === 0 ||
      storedMessages.length > 0;

    if (!shouldReconcile) {
      if (storedMessages.length > 0) {
        creatingSessionFromSendRef.current = false;
      }
      return;
    }

    setMessages((currentMessages) => {
      const nextMessages = reconcileCampaignTranscript(
        currentMessages,
        storedMessages,
        {
          allowFullReplace:
            isSessionSwitch ||
            currentMessages.length === 0 ||
            needsServerMessageHydration(storedMessages, currentMessages),
          chatStatus: status,
        },
      );

      if (messagesFingerprint(nextMessages) === messagesFingerprint(currentMessages)) {
        return currentMessages;
      }

      return nextMessages;
    });
    hydratedSessionIdRef.current = activeSessionId;

    if (storedMessages.length > 0) {
      creatingSessionFromSendRef.current = false;
    }
  }, [
    activeSessionId,
    sessionDetail,
    setMessages,
    status,
  ]);

  useEffect(() => {
    const previousStatus = previousChatStatusRef.current;
    previousChatStatusRef.current = status;

    if (!activeSessionId || !refetch) {
      return;
    }

    if (
      status === 'ready' &&
      (previousStatus === 'streaming' || previousStatus === 'submitted')
    ) {
      void refetch();
    }
  }, [activeSessionId, refetch, status]);

  const sendMessage = useCallback(
    (payload) => {
      const isStringPayload = typeof payload === 'string';
      const text = isStringPayload ? payload : payload?.text;
      const files = isStringPayload ? undefined : payload?.files;

      const trimmedText = text?.trim() ?? '';
      const hasFiles = Array.isArray(files) && files.length > 0;

      if (!trimmedText && !hasFiles) return;

      creatingSessionFromSendRef.current = !sessionIdRef.current;

      if (hasFiles && trimmedText) {
        return chatSendMessage({text: trimmedText, files});
      }

      if (hasFiles) {
        return chatSendMessage({files});
      }

      return chatSendMessage({text: trimmedText});
    },
    [chatSendMessage],
  );

  const isLoading = status === 'streaming' || status === 'submitted';
  const isChatExpanded =
    Boolean(activeSessionId) || messages.length > 0 || isLoading;

  return {
    messages,
    isChatExpanded,
    isLoading,
    error,
    sendMessage,
    setMessages,
    status,
    regenerate,
    clearError,
    onCampaignLimitReachedRef,
  };
}
